// PUT a file to a signed storage URL and report upload progress. Uses
// XMLHttpRequest because fetch exposes no upload progress events.

export type UploadFailureReason = "network" | "http" | "aborted";

/** An upload that did not complete: the connection dropped, storage answered non-2xx, or it was cancelled. */
export type UploadFailure = Error & {
  readonly reason: UploadFailureReason;
  /** Storage's HTTP status, for `http` failures. */
  readonly status?: number;
};

const UPLOAD_FAILURE_NAME = "UploadFailure";

function uploadFailure(
  reason: UploadFailureReason,
  message: string,
  status?: number,
): UploadFailure {
  return Object.assign(new Error(message), {
    name: UPLOAD_FAILURE_NAME,
    reason,
    ...(status !== undefined ? { status } : {}),
  });
}

export function isUploadFailure(value: unknown): value is UploadFailure {
  return (
    value instanceof Error &&
    value.name === UPLOAD_FAILURE_NAME &&
    "reason" in value
  );
}

/**
 * How long an upload may go without a progress event before it counts as a
 * dropped connection. A half-open socket (e.g. a Wi-Fi to cellular switch)
 * never fires `error`, and would otherwise hold up the queue forever.
 */
export const UPLOAD_STALL_TIMEOUT_MS = 60_000;

export interface PutFileWithProgressOptions {
  url: string;
  file: File;
  contentType: string;
  /** Bytes sent so far and the total, as the browser reports them. */
  onProgress?: (loaded: number, total: number) => void;
  signal?: AbortSignal;
  /** Defaults to {@link UPLOAD_STALL_TIMEOUT_MS}. */
  stallTimeoutMs?: number;
}

export function putFileWithProgress({
  url,
  file,
  contentType,
  onProgress,
  signal,
  stallTimeoutMs = UPLOAD_STALL_TIMEOUT_MS,
}: PutFileWithProgressOptions): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(uploadFailure("aborted", "Upload cancelled"));
      return;
    }

    const xhr = new XMLHttpRequest();
    let stalled = false;
    let stallTimer: ReturnType<typeof setTimeout> | undefined;
    const restartStallTimer = () => {
      clearTimeout(stallTimer);
      stallTimer = setTimeout(() => {
        stalled = true;
        xhr.abort();
      }, stallTimeoutMs);
    };
    const abort = () => xhr.abort();
    signal?.addEventListener("abort", abort, { once: true });
    const settle = (finish: () => void) => {
      clearTimeout(stallTimer);
      signal?.removeEventListener("abort", abort);
      finish();
    };

    xhr.upload.addEventListener("progress", (event) => {
      restartStallTimer();
      onProgress?.(
        event.loaded,
        event.lengthComputable ? event.total : file.size,
      );
    });
    xhr.addEventListener("load", () => {
      settle(() => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else
          reject(
            uploadFailure(
              "http",
              `Upload rejected with status ${xhr.status}`,
              xhr.status,
            ),
          );
      });
    });
    xhr.addEventListener("error", () => {
      settle(() =>
        reject(uploadFailure("network", "Upload connection was lost")),
      );
    });
    xhr.addEventListener("abort", () => {
      settle(() =>
        reject(
          stalled
            ? uploadFailure("network", "Upload stalled with no progress")
            : uploadFailure("aborted", "Upload cancelled"),
        ),
      );
    });

    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.send(file);
    restartStallTimer();
  });
}
